/* ==========================================================================
   DAM SAINTS — theme.js
   Vanilla JS only. No framework, no build step, no WebGL.
   Handles: sticky/transparent header · scroll reveal · mobile menu ·
            search overlay + predictive search · cart drawer · quick add ·
            quantity · variant selection · accordion · newsletter states.
   Every behaviour degrades gracefully and respects prefers-reduced-motion.
   ========================================================================== */
(function () {
  'use strict';
  var dsCopyElement = document.getElementById('DSCopyStrings');
  var dsCopy = dsCopyElement ? JSON.parse(dsCopyElement.textContent) : {};


  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),select,textarea,[tabindex]:not([tabindex="-1"])';

  function $(s, r) { return (r || document).querySelector(s); }
  function $$(s, r) { return Array.prototype.slice.call((r || document).querySelectorAll(s)); }
  function on(el, ev, fn) { if (el) el.addEventListener(ev, fn); }

  /* ---------- 1. HEADER — transparent over hero, solid after scroll ------
     Bound once (module-level flag) and always re-queries the live header
     node inside update(). Section reloads used to call initHeader() again,
     which attached a brand-new window 'scroll' listener every time without
     removing the old one — after a few edits in the theme editor dozens of
     stale listeners piled up on window, each doing DOM work every frame,
     which is what made scrolling feel less and less fluid over a session. */
  var headerBound = false;
  function updateHeader() {
    var header = $('[data-ds-header]');
    if (!header) return;
    var inverse = header.classList.contains('ds-header--overlay') && window.scrollY <= Number(header.dataset.solidAfter || 80);
    header.classList.toggle('is-solid', !inverse);
    header.classList.toggle('ds-inverse', inverse);
  }
  function initHeader() {
    updateHeader();
    if (headerBound) return;
    headerBound = true;
    var queued = false;
    on(window, 'scroll', function () {
      if (queued) return;
      queued = true;
      requestAnimationFrame(function () { updateHeader(); queued = false; });
    });
  }

  /* ---------- 2. EDITORIAL SCROLL REVEAL --------------------------------
     Reuses a single IntersectionObserver across re-inits instead of
     constructing a new one every time a section reloads, and skips items
     already bound so a re-run only picks up freshly inserted markup. */
  var revealObserver = null;
  function initReveal() {
    var items = $$('.ds-reveal').filter(function (el) { return !el.hasAttribute('data-ds-bound'); });
    if (!items.length) return;
    if (REDUCED || document.body.dataset.dsMotion === 'false' || !('IntersectionObserver' in window)) {
      items.forEach(function (el) { el.classList.add('is-visible'); el.setAttribute('data-ds-bound', ''); });
      return;
    }
    if (!revealObserver) {
      revealObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) { e.target.classList.add('is-visible'); revealObserver.unobserve(e.target); }
        });
      }, { rootMargin: '0px 0px -12% 0px', threshold: 0.15 });
    }
    items.forEach(function (el) { el.setAttribute('data-ds-bound', ''); revealObserver.observe(el); });
  }

  /* ---------- 3. PANELS (menu / search / drawer) ------------------------ */
  var openPanel = null, openScrim = null, lastFocus = null, inertNodes = [];
  function restoreBackground() {
    inertNodes.forEach(function (el) { el.inert = false; });
    inertNodes = [];
  }
  function isolatePanel(panel) {
    var node = panel;
    while (node && node.parentElement) {
      Array.prototype.forEach.call(node.parentElement.children, function (sibling) {
        if (sibling !== node && !sibling.inert && !sibling.matches('script,style,[data-ds-overlay]')) {
          sibling.inert = true; inertNodes.push(sibling);
        }
      });
      node = node.parentElement;
      if (node === document.body) break;
    }
  }
  function setTriggers(panel, expanded) {
    $$('[data-ds-open]').forEach(function (trigger) {
      if ($(trigger.getAttribute('data-ds-open')) === panel) trigger.setAttribute('aria-expanded', String(expanded));
    });
  }

  function trap(e) {
    if (!openPanel || e.key !== 'Tab') return;
    var f = $$(FOCUSABLE, openPanel).filter(function (el) { return el.offsetParent !== null; });
    if (!f.length) return;
    var first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  function show(panel) {
    if (!panel) return;
    if (openPanel === panel) return;
    if (openPanel && openPanel !== panel) hide(openPanel);
    lastFocus = document.activeElement;
    panel.classList.add('is-open');
    panel.removeAttribute('aria-hidden');
    if (panel.tagName === 'DIALOG') {
      panel.showModal();
      if (!panel.dataset.dsCancelBound) {
        panel.dataset.dsCancelBound = 'true';
        on(panel, 'cancel', function (event) { event.preventDefault(); hide(panel); });
      }
    } else isolatePanel(panel);
    setTriggers(panel, true);
    // Remember which scrim belongs to THIS panel, so hide() closes the same
    // one — previously hide() always looked up the default [data-ds-overlay],
    // so a panel opened with a custom data-scrim left its own scrim stuck
    // open (dark, unclickable overlay) after closing.
    openScrim = panel.getAttribute('data-scrim') ? $(panel.getAttribute('data-scrim')) : $('[data-ds-overlay]');
    if (openScrim) openScrim.classList.add('is-open');
    document.documentElement.style.overflow = 'hidden';
    openPanel = panel;
    var target = $('[data-autofocus]', panel) || $$(FOCUSABLE, panel)[0];
    if (target) requestAnimationFrame(function () {
      requestAnimationFrame(function () { if (openPanel === panel) target.focus({ preventScroll: true }); });
    });
    document.addEventListener('keydown', trap);
  }

  function hide(panel) {
    panel = panel || openPanel;
    if (!panel) return;
    panel.classList.remove('is-open');
    panel.setAttribute('aria-hidden', 'true');
    if (panel.tagName === 'DIALOG' && panel.open) panel.close();
    setTriggers(panel, false);
    restoreBackground();
    if (openScrim) openScrim.classList.remove('is-open');
    openScrim = null;
    document.documentElement.style.overflow = '';
    document.removeEventListener('keydown', trap);
    openPanel = null;
    if (lastFocus && lastFocus.isConnected && lastFocus.focus) lastFocus.focus();
  }

  // Delegated to document and bound once: dynamically injected open/close
  // triggers work with no extra wiring, and repeated init() calls (fired on
  // every shopify:section:load) can no longer stack duplicate listeners.
  var panelsBound = false;
  function initPanels() {
    if (panelsBound) return;
    panelsBound = true;
    on(document, 'click', function (e) {
      var openBtn = e.target.closest('[data-ds-open]');
      if (openBtn) { var targetPanel = $(openBtn.getAttribute('data-ds-open')); if (targetPanel) { e.preventDefault(); show(targetPanel); } return; }
      var closeBtn = e.target.closest('[data-ds-close]');
      if (closeBtn) { e.preventDefault(); hide(); return; }
      if (e.target.closest('[data-ds-overlay]')) hide();
    });
    on(document, 'keydown', function (e) { if (e.key === 'Escape') { if (openPanel) { e.preventDefault(); hide(); } else $$('.dse-nav-branch[open]').forEach(function (branch) { branch.open = false; $('summary', branch).focus(); }); } });
  }

  /* ---------- 4. PREDICTIVE SEARCH (Shopify Search & Discovery API) ----- */
  function initPredictiveSearch() {
    var input = $('[data-ds-search-input]');
    var results = $('[data-ds-search-results]');
    if (!input || !results || input.hasAttribute('data-ds-bound')) return;
    input.setAttribute('data-ds-bound', '');
    var timer = null, controller = null;
    on(input, 'input', function () {
      var q = input.value.trim();
      clearTimeout(timer);
      if (controller) controller.abort();
      if (q.length < 2) { results.innerHTML = ''; results.removeAttribute('aria-busy'); return; }
      results.setAttribute('aria-busy', 'true');
      timer = setTimeout(function () {
        controller = new AbortController();
        var url = ROOT + 'search/suggest?q=' + encodeURIComponent(q) +
          '&resources[type]=product,collection,article&resources[limit]=6' +
          '&section_id=ds-predictive-search';
        fetch(url, { signal: controller.signal })
          .then(function (r) { return r.text(); })
          .then(function (html) {
            var doc = new DOMParser().parseFromString(html, 'text/html');
            var inner = doc.querySelector('[data-ds-search-results]');
            results.innerHTML = inner ? inner.innerHTML : '';
            results.setAttribute('aria-busy', 'false');
          })
          .catch(function (err) {
            if (err && err.name === 'AbortError') return; // superseded by a newer keystroke
            results.setAttribute('aria-busy', 'false');
          });
      }, 250);
    });
  }

  /* ---------- 5. CART — quick add, drawer refresh, quantity, remove ----- */
  function refreshDrawer() {
    return fetch(ROOT + '?section_id=ds-cart-drawer')
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var fresh = doc.querySelector('[data-ds-cart-drawer]');
        var live = $('[data-ds-cart-drawer]');
        if (fresh && live) {
          var focus = document.activeElement;
          var key = focus && focus.closest('[data-line-key]');
          var previousKey = key && key.getAttribute('data-line-key');
          var focusWasInside = live.contains(focus);
          var wasQuantity = focus && focus.matches('.ds-qty__input');
          live.innerHTML = fresh.innerHTML;
          if (focusWasInside && openPanel === live) {
            var nextFocus = null;
            if (previousKey) {
              var row = $$('[data-line-key]', live).find(function (el) { return el.dataset.lineKey === previousKey; });
              if (row) nextFocus = $(wasQuantity ? 'input' : 'button', row);
            }
            (nextFocus || $('[data-ds-close]', live) || live).focus();
          }
        }
        var count = doc.querySelector('[data-ds-cart-count]');
        $$('[data-ds-cart-count]').forEach(function (el) {
          if (count) el.textContent = count.textContent;
        });
      });
  }

  function toast(message) {
    var t = $('[data-ds-toast]');
    if (!t) return;
    var label = $('[data-ds-toast-text]', t);
    if (label && message) label.textContent = message;
    t.classList.add('is-open');
    clearTimeout(t._timer);
    t._timer = setTimeout(function () { t.classList.remove('is-open'); }, 2600);
  }

  function addToCart(variantId, quantity, button) {
    if (button) { button.classList.add('is-loading'); button.setAttribute('aria-busy', 'true'); }
    return fetch(ROOT + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: quantity || 1 }] })
    })
      .then(function (r) { return r.json().then(function (d) { if (!r.ok) throw d; return d; }); })
      .then(function () { return refreshDrawer(); })
      .then(function () {
        if (button) { button.classList.remove('is-loading'); button.removeAttribute('aria-busy'); }
        toast('Added to cart');
        var drawer = $('[data-ds-cart-drawer]');
        if (drawer && drawer.getAttribute('data-open-on-add') === 'true') show(drawer);
      })
      .catch(function (err) {
        if (button) { button.classList.remove('is-loading'); button.removeAttribute('aria-busy'); }
        toast((err && err.description) || dsCopy.cart_add_error);
      });
  }

  var changingCart = false;
  function changeLine(key, quantity) {
    if (changingCart || !key || !Number.isFinite(quantity)) return Promise.resolve();
    changingCart = true;
    var drawer = $('[data-ds-cart-drawer]');
    if (drawer) drawer.setAttribute('aria-busy', 'true');
    return fetch(ROOT + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: Math.max(0, Math.floor(quantity)) })
    }).then(function (r) {
      return r.json().then(function (data) { if (!r.ok) throw data; return data; });
    }).then(refreshDrawer).catch(function (err) {
      toast((err && err.description) || dsCopy.cart_update_error);
      return refreshDrawer().catch(function () {});
    }).finally(function () {
      changingCart = false;
      if (drawer) drawer.removeAttribute('aria-busy');
    });
  }

  // Both handlers are delegated to document and bound once: product forms
  // and quick-add buttons keep working after a section reload without
  // re-registering a listener per element every time, and an in-flight
  // add-to-cart now blocks a second click/submit instead of firing twice.
  var cartBound = false;
  function initCart() {
    if (cartBound) return;
    cartBound = true;
    on(document, 'click', function (e) {
      var qa = e.target.closest('[data-ds-quick-add]');
      if (qa) {
        e.preventDefault();
        if (qa.classList.contains('is-loading')) return;
        addToCart(qa.getAttribute('data-variant-id'), 1, qa);
        return;
      }
      var rm = e.target.closest('[data-ds-line-remove]');
      if (rm) { e.preventDefault(); changeLine(rm.getAttribute('data-line-key'), 0); return; }
      var step = e.target.closest('[data-ds-qty-step]');
      if (step) {
        e.preventDefault();
        if (changingCart) return;
        var wrap = step.closest('[data-ds-qty]');
        var field = $('input', wrap);
        var min = parseInt(field.getAttribute('min') || '1', 10);
        var next = Math.max(min, parseInt(field.value || '1', 10) + parseInt(step.getAttribute('data-ds-qty-step'), 10));
        field.value = next;
        field.dispatchEvent(new Event('change', { bubbles: true }));

      }
    });

    on(document, 'change', function (event) {
      var input = event.target.closest('[data-ds-cart-drawer] [data-ds-qty] input');
      if (!input) return;
      var qty = Number(input.value);
      if (!Number.isFinite(qty) || qty < 0 || !Number.isInteger(qty)) { refreshDrawer(); return; }
      changeLine(input.closest('[data-line-key]').dataset.lineKey, qty);
    });

    // Product form (add to cart) — intercept for drawer flow
    on(document, 'submit', function (e) {
      var form = e.target.closest('[data-ds-product-form]');
      if (!form) return;
      e.preventDefault();
      var submit = $('[type="submit"]', form);
      if (submit && submit.classList.contains('is-loading')) return;
      var id = $('[name="id"]', form);
      var qty = $('[name="quantity"]', form);
      addToCart(id && id.value, qty ? parseInt(qty.value, 10) : 1, submit);
    });
  }

  /* ---------- 6. VARIANT SELECTION -------------------------------------- */
  function initVariants() {
    $$('[data-ds-variant-picker]').forEach(function (picker) {
      if (picker.hasAttribute('data-ds-bound')) return;
      picker.setAttribute('data-ds-bound', '');
      var form = document.getElementById(picker.getAttribute('data-form-id'));
      var jsonEl = $('[data-ds-variant-json]', picker);
      var variants = JSON.parse((jsonEl && jsonEl.textContent) || '[]');
      on(picker, 'click', function (e) {
        var chip = e.target.closest('[data-ds-variant-option]');
        if (!chip) return;
        var group = chip.closest('[data-ds-variant-group]');
        $$('[data-ds-variant-option]', group).forEach(function (c) {
          c.classList.toggle('is-selected', c === chip);
          c.setAttribute('aria-pressed', c === chip ? 'true' : 'false');
        });
        var chosen = $$('[data-ds-variant-group]', picker).map(function (g) {
          var sel = $('.is-selected', g);
          return sel ? sel.getAttribute('data-value') : null;
        });
        var match = variants.filter(function (v) {
          return chosen.every(function (val, i) { return !val || v.options[i] === val; });
        })[0];
        var idField = form && $('[name="id"]', form);
        var submit = form && $('[type="submit"]', form);
        var productSection = picker.closest('.ds-product') || document;
        var priceEl = $('[data-ds-price]', productSection);
        var availEl = $('[data-ds-availability]', productSection);
        if (idField) idField.value = match ? match.id : '';
        if (submit) {
          var ok = match && match.available;
          submit.disabled = !ok;
          submit.textContent = ok ? 'Add to cart' : (match ? 'Sold out' : 'Unavailable');
        }
        if (match && priceEl && match.price_formatted) {
          priceEl.textContent = match.price_formatted;
          if (match.on_sale && match.compare_formatted) {
            var compare = document.createElement('span');
            compare.className = 'ds-product__price-compare'; compare.textContent = match.compare_formatted;
            priceEl.appendChild(compare);
          }
        }
        if (availEl) {
          availEl.classList.toggle('is-in-stock', !!(match && match.available));
          availEl.textContent = match ? (match.available ? availEl.dataset.inStockText || 'In stock' : 'Sold out') : 'Unavailable';
        }
        if (match && window.history.replaceState) {
          var url = new URL(window.location.href);
          url.searchParams.set('variant', match.id);
          window.history.replaceState({}, '', url);
        }
      });
    });
  }

  /* ---------- 7. ACCORDION ---------------------------------------------
     Panels driven open/closed by inline max-height. Two bugs fixed:
     1. A trigger server-rendered with aria-expanded="true" never got its
        panel's max-height set (only the click handler set it), so it
        rendered visually collapsed despite claiming to be open.
     2. max-height was a frozen pixel value — if content reflowed (fonts
        loading, viewport rotation/resize) an open panel stayed clipped to
        its old height instead of growing with its content. */
  var accordionResizeBound = false;
  function initAccordions() {
    $$('[data-ds-accordion] .ds-accordion__trigger').forEach(function (trigger) {
      if (trigger.hasAttribute('data-ds-bound')) return;
      trigger.setAttribute('data-ds-bound', '');
      var panel = document.getElementById(trigger.getAttribute('aria-controls'));
      if (panel && trigger.getAttribute('aria-expanded') === 'true') {
        panel.classList.add('is-open');
        panel.style.maxHeight = panel.scrollHeight + 'px';
      }
      on(trigger, 'click', function () {
        var open = trigger.getAttribute('aria-expanded') === 'true';
        trigger.setAttribute('aria-expanded', open ? 'false' : 'true');
        if (!panel) return;
        panel.classList.toggle('is-open', !open);
        panel.style.maxHeight = open ? '0px' : panel.scrollHeight + 'px';
      });
    });

    if (!accordionResizeBound) {
      accordionResizeBound = true;
      var resizeTimer = null;
      on(window, 'resize', function () {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(function () {
          $$('.ds-accordion__panel.is-open').forEach(function (panel) {
            panel.style.maxHeight = panel.scrollHeight + 'px';
          });
        }, 150);
      });
    }
  }

  /* ---------- 8. PRODUCT GALLERY --------------------------------------- */
  function initGallery() {
    $$('[data-ds-gallery]').forEach(function (gallery) {
      if (gallery.hasAttribute('data-ds-bound')) return;
      gallery.setAttribute('data-ds-bound', '');
      var main = $('[data-ds-gallery-main] img', gallery);
      on(gallery, 'click', function (e) {
        var thumb = e.target.closest('[data-ds-gallery-thumb]');
        if (!thumb || !main) return;
        main.src = thumb.getAttribute('data-full');
        main.srcset = thumb.getAttribute('data-srcset') || '';
        main.alt = thumb.getAttribute('data-alt') || main.alt;
        $$('[data-ds-gallery-thumb]', gallery).forEach(function (t) {
          t.classList.toggle('is-active', t === thumb);
          t.setAttribute('aria-current', t === thumb ? 'true' : 'false');
        });
      });
    });
  }

  /* ---------- 9. NEWSLETTER STATES ------------------------------------- */
  function initNewsletter() {
    $$('[data-ds-newsletter]').forEach(function (form) {
      if (form.hasAttribute('data-ds-bound')) return;
      form.setAttribute('data-ds-bound', '');
      var input = $('input[type="email"]', form);
      var msg = $('[data-ds-newsletter-msg]', form);
      on(form, 'submit', function (e) {
        // Client-side format check only; Shopify handles the real submit.
        if (!input || /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(input.value.trim())) return;
        e.preventDefault();
        input.classList.add('is-error');
        input.setAttribute('aria-invalid', 'true');
        if (msg) {
          msg.textContent = 'Enter a valid email address.';
          msg.className = 'ds-newsletter__msg ds-newsletter__msg--error';
        }
        input.focus();
      });
      on(input, 'input', function () {
        input.classList.remove('is-error');
        input.removeAttribute('aria-invalid');
        if (msg && msg.classList.contains('ds-newsletter__msg--error')) msg.textContent = '';
      });
    });
  }

  function init() {
    initHeader(); initReveal(); initPanels(); initPredictiveSearch();
    initCart(); initVariants(); initAccordions(); initGallery(); initNewsletter();
  }

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);

  // Shopify Theme Editor re-renders sections without a page reload.
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:unload', function (event) {
    if (openPanel && event.target.contains(openPanel)) hide();
    if (revealObserver) $$('.ds-reveal', event.target).forEach(function (el) { revealObserver.unobserve(el); });
  });

  window.DamSaints = { addToCart: addToCart, refreshDrawer: refreshDrawer, toast: toast, openPanel: show, closePanel: hide };
})();
